import { formatNumber } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { UrlTree } from '@angular/router';
import { get, initial, isNil, isNumber, last, range, sum } from 'lodash-es';
import { Observable } from 'rxjs';
import { MultiSelectDropdownComponent, Option } from '@cumlaude/shared-components-inputs';
import { aggDecimals, maxOver, noAgg, noAgg0, noAggString, SingleAggregator, sumOver, weightedAverage } from '../../services/aggregation';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { CijferMeasure, DataOptions, DataResponse, DataService, ExportDataOptions, FilterExpression } from '../../services/data.service';
import { att, percOfRow } from '../../services/measures';
import { LabelCellComponent } from '../../shared/components/table/cells/label-cell/label-cell.component';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { deelVeilig, generateCssClassForCijfer } from '@cumlaude/shared-utils';
import { BarInfo } from '../../services/stacked-bars';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { Att, AttrPath, Table } from '@cumlaude/metadata';
import { Axis } from '../../services/axis';
import { CijferColumn, DashboardVariant } from '../../services/weergave-opties';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { getBijzonderheidLabel } from '../../services/cijfers';
import { InstellingBron } from '@cumlaude/service-contract';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface CijferHistogram extends Attributes {
	/** Histogram met aantallen per afgerond cijfer */
	cf_m_nr_aantal_0: number;
	cf_m_nr_aantal_1: number;
	cf_m_nr_aantal_2: number;
	cf_m_nr_aantal_3: number;
	cf_m_nr_aantal_4: number;
	cf_m_nr_aantal_5: number;
	cf_m_nr_aantal_6: number;
	cf_m_nr_aantal_7: number;
	cf_m_nr_aantal_8: number;
	cf_m_nr_aantal_9: number;
	cf_m_nr_aantal_10: number;
}

export interface CijferI extends CijferHistogram {
	cf_m_nr_cijfer_afgerond: number;
	cf_m_nr_cijfer: number;
	cf_m_nr_decimalen: number;
	cf_m_nr_cijfer_vorig_sj: number;
	cf_m_nr_gem_cijfer_andere_vakken: number;
	cf_m_nr_tekortpunten: number;
	cf_m_nr_leerlingen: number;
	cf_m_nr_cijfer_mediaan: number;
	cf_m_nr_perc_geoorloofd_afwezig: number;
	cf_m_nr_perc_ongeoorloofd_afwezig: number;
	cf_m_nr_sectiegemiddelde: number;
	cf_m_nr_plaatsing_advies_score: number;
	cf_m_nr_aantal_toetsen: number;
	cf_m_is_gevuld: number;
	cf_m_nr_label_onvoldoendes: number; // | null
	clc_m_abb_label_avg: any;
	cf_m_nm_bijzonderheid: any;
}

export interface CijferA extends Attributes {
	cijfer: number | null;
	cijferVorigSchooljaar: number | null;
	cijferAndereVakken: number | null;
	decimalen: number;
	label: string;
	bijzonderheid: string;
	tekortpunten: number;
	tekortpuntenPerLeerling: number;
	aantalLeerlingen: number;
	onvoldoendes: number;
	cijferAfgerondOV: any; // number | 'onvoldoende' | 'voldoende';
	cijferMediaan: number | null;
	percGeoorloofdAfwezig: number | null;
	percOngeoorloofdAfwezig: number | null;
	sectiegemiddelde: number;
	plaatsingAdviesScore: number | null;
	gemiddeldAantalToetsen: number | null;
	max: number;
	aantalCijfers: number;
	cf_nr_label_onvoldoendes: number | null;
}

export function cijferOpties(resultaat: number | string): string {
	if (isNumber(resultaat))
		return ['nullen', 'enen', 'tweeën', 'drieën', 'vieren', 'vijven', 'zessen', 'zevens', 'achten', 'negens', 'tienen'][resultaat];
	return `${resultaat}s`; // voldoendes/onvoldoendes
}

const aanwezigheidColumns = [CijferColumn.PercAfwezigheidGeoorloofd, CijferColumn.PercAfwezigheidOngeoorloofd];

@Component({
	selector: 'app-cijfers-overzicht',
	templateUrl: './cijfers-overzicht.component.html',
	styleUrls: ['./cijfers-overzicht.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		MultiSelectDropdownComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		FilterBarComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CijfersOverzichtComponent extends BarchartTableConfig<CijferI, CijferA> implements OnInit {
	protected readonly dataService = inject(DataService);

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	historieSubgroups = computed(() => [...this.groeperingService.huidigeGroeperingen().slice(-1), ['cf_nm_schooljaar'] as AttrPath]);

	override availableGroups: AttrPath[] = [
		['cf_fk_ll', 'll_nm_basisschooladvies'],
		['cf_fk_ll', 'll_nm_svh'],
		['cf_fks_mw', 'mw_nm_medewerker'],
		['cf_nm_klas'],
		['cf_nr_leerjaar'],
		['cf_nm_leerling'],
		['cf_nm_lesgroep'],
		['cf_nm_lesgroep_docenten'],
		['cf_nm_opleiding'],
		['cf_fk_ilt', 'ilt_nm_niveau'],
		['cf_fk_ilt_vorig_sj', 'ilt_nm_niveau'],
		['cf_fk_ilt', 'ilt_abb_profiel'],
		['cf_nm_vak_uni'],
		['cf_nm_vak'],
		['cf_nr_leerjaar_vak'],
		['cf_abb_onderwijssoort_vak'],
		['cf_fk_vk', 'vk_nm_vakkengroep'],
		['cf_fk_vk', 'vk_nm_vak'],
		['cf_nm_vestiging'],
		['cf_fk_lb_vorig_sj', 'lb_nm_vestiging'],
		['cf_fk_lb', 'lb_nm_leerfase'],
		['cf_fk_lb_vorig_sj', 'lb_nm_leerfase'],
		['cf_fun_nm_periode'],
		['cf_abb_kolomkop'],
		['cf_nr_kolom'],
	];

	actueelFilters: FilterName[] = [
		'cf_nm_schooljaar',
		'cf_co_brin',
		'cf_nm_vestiging',
		'cf_fk_ilt.ilt_nm_niveau',
		'cf_nr_leerjaar',
		'x_cijfertype',
		'x_gem_periode',
		'cf_nm_vak',
		'cf_nm_lesgroep',
		'cf_is_alternatievenormering',
	];

	historieFilters: FilterName[] = [
		'x_cijfer_schooljaar_historie', //
		'x_cijfer_multiselect_schooljaar',
		...this.actueelFilters.filter((f) => !['cf_nm_lesgroep'].includes(f)),
	];

	permanentFilterExpressions: FilterExpression[] = [];

	variant = this.qp.signal('variant');

	factTable = Table.fac_cf_cijfer;

	protected override singleAggregators: Partial<{ [ai in keyof CijferA]: SingleAggregator<CijferI, CijferA[ai]> }> = {
		aantalLeerlingen: noAgg0('cf_m_nr_leerlingen'),
		tekortpunten: sumOver('cf_m_nr_tekortpunten'),
		onvoldoendes: {
			init: (i: CijferI) => i.cf_m_nr_label_onvoldoendes ?? sum(range(0, 6).map((n) => get(i, 'cf_m_nr_aantal_' + n, 0))),
			combine: (as: number[]) => sum(as),
		},
		max: maxOver('cf_m_nr_cijfer'),
		aantalCijfers: sumOver('cf_m_is_gevuld'),
		cf_nr_label_onvoldoendes: sumOver('cf_m_nr_label_onvoldoendes'),
		cijferMediaan: noAgg('cf_m_nr_cijfer_mediaan'),
		percGeoorloofdAfwezig: noAgg('cf_m_nr_perc_geoorloofd_afwezig'),
		percOngeoorloofdAfwezig: noAgg('cf_m_nr_perc_ongeoorloofd_afwezig'),
		decimalen: aggDecimals('cf_m_nr_decimalen'),
		label: noAggString('clc_m_abb_label_avg'),
		bijzonderheid: noAggString('cf_m_nm_bijzonderheid'),
	};

	protected override multiAggregators = [
		weightedAverage('cijfer', 'cf_m_nr_cijfer', 'aantalCijfers'),
		weightedAverage('cijferVorigSchooljaar', 'cf_m_nr_cijfer_vorig_sj'),
		weightedAverage('cijferAndereVakken', 'cf_m_nr_gem_cijfer_andere_vakken'),
		weightedAverage('sectiegemiddelde', 'cf_m_nr_sectiegemiddelde'),
		weightedAverage('plaatsingAdviesScore', 'cf_m_nr_plaatsing_advies_score'),
		weightedAverage('gemiddeldAantalToetsen', 'cf_m_nr_aantal_toetsen'),
	];

	private readonly cijferValue = (path: Path<CijferA, number[]>) => {
		const { label, bijzonderheid, cijfer } = getLeafA(path);
		const bijzonderheidLabel = getBijzonderheidLabel(bijzonderheid, cijfer, label);
		return bijzonderheidLabel ?? label ?? cijfer;
	};

	private readonly cijferFormat = (path: Path<CijferA, number[]>) => {
		const { decimalen } = getLeafA(path);
		return `1.${decimalen}-${decimalen}`;
	};

	private readonly cijferType = (path: Path<CijferA, number[]>) => {
		const { label, bijzonderheid } = getLeafA(path);
		return label || bijzonderheid ? 'string' : 'number';
	};

	columnMultiSelectOptions: Option<CijferColumn>[] = Object.values(CijferColumn).map((column) => new Option<CijferColumn>(column));

	visibleColumns!: CijferColumn[];

	constructor() {
		super();

		if (this.userService.bron() === InstellingBron.Magister) {
			this.actueelFilters.push('cf_abb_kolomkop', 'cf_nr_kolom');
		}
	}

	ngOnInit() {
		this.subscribeToQueryParams();
	}

	subscribeToQueryParams() {
		this.subscriptions.push(this.qp.observe('col').subscribe((cols) => this.setVisibleColumns(cols)));
	}

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getCijfersData({
			...options,
			m: [
				CijferMeasure.HISTOGRAM,
				CijferMeasure.RELATIE,
				CijferMeasure.STATS,
				CijferMeasure.LABEL,
				CijferMeasure.BIJZONDERHEID,
				...(this.visibleColumns?.includes(CijferColumn.PlaatsingAdvies) ? [CijferMeasure.PLAATSING_ADVIES] : []),
				...(this.visibleColumns?.some((column) => aanwezigheidColumns.includes(column)) ? [CijferMeasure.AANWEZIGHEID] : []),
			],
			r: range(0, this.getRowLevel() + 1),
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getCijfersExportData(options);
	}

	override createMeasureColumns(context: DashboardContext<CijferI, CijferA, CijfersOverzichtComponent>): ColumnDef<DataRow<CijferA>>[] {
		if (this.variant() !== DashboardVariant.ACTUEEL) return [];

		const columns: ColumnDef<DataRow<CijferA>>[] = [
			createMeasureColumn(CijferColumn.AantalCijfers, att('aantalCijfers')),
			createMeasureColumn(CijferColumn.Cijfer, this.cijferValue, { format: this.cijferFormat, dataType: this.cijferType }),
			createMeasureColumn(CijferColumn.GemAndereVakken, att('cijferAndereVakken'), { format: '1.1-1' }),
			createMeasureColumn(CijferColumn.Sectiegemiddelde, att('sectiegemiddelde'), { format: '1.1-1' }),
			createMeasureColumn(CijferColumn.GemAantalToetsen, att('gemiddeldAantalToetsen')),
			createMeasureColumn(CijferColumn.CijferVorigSchooljaar, att('cijferVorigSchooljaar'), { format: '1.1-1' }),
			createMeasureColumn(CijferColumn.Tekortpunten, att('tekortpunten')),
			createMeasureColumn(CijferColumn.GemTekortpunten, percOfRow('tekortpunten', 'aantalLeerlingen')),
			this.createLeerlingColumn(CijferColumn.Leerlingen, att('aantalLeerlingen'), { context }),
			createMeasureColumn(CijferColumn.PercOnvoldoende, percOfRow('onvoldoendes', 'aantalCijfers'), { dataType: 'percentage' }),
			createMeasureColumn(CijferColumn.Mediaan, att('cijferMediaan'), { format: '1.1-1' }),
			createMeasureColumn(
				CijferColumn.PlaatsingAdvies,
				(path) => {
					const score = getLeafA(path).plaatsingAdviesScore;
					if (isNil(score)) return '-';
					const formatted = formatNumber(score, 'nl-NL', '1.1-1');
					if (score < -0.05) return `${formatted} (onder adv.)`;
					if (score >= 0.05) return `+${formatted} (boven adv.)`;
					return '0,0 (op adv.)';
				},
				{ component: LabelCellComponent }
			),
			createMeasureColumn(CijferColumn.PercAfwezigheidOngeoorloofd, att('percOngeoorloofdAfwezig'), {
				dataType: 'percentage',
				format: '1.2-2',
			}),
			createMeasureColumn(CijferColumn.PercAfwezigheidGeoorloofd, att('percGeoorloofdAfwezig'), {
				dataType: 'percentage',
				format: '1.2-2',
			}),
		];

		// Overschrijf default visibile en exportable die createMeasureColumn heeft gezet
		columns.forEach((col) => {
			col.visible = this.visibleColumns.includes(<CijferColumn>col.column);
			col.exportable = col.visible;
		});

		return columns;
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	setVisibleColumns(columns: CijferColumn[]) {
		this.visibleColumns = columns;
		this.filterService.refresh();
	}

	override getBarchartQty(path: Path<CijferA, number[]>): number {
		return Math.min(getLeafA(path).cijfer ?? 0, 10);
	}

	override createXAxis(_context: DashboardContext<CijferI, CijferA, CijfersOverzichtComponent>): Axis {
		return { min: 0, max: 10, ticks: [] };
	}

	override partitionBarData(rowRoot: Level<CijferA, number[]>): Path<CijferA, number[]>[][] {
		return partitionHistogram(rowRoot);
	}

	override makeBar(
		attrs: CijferI,
		path: Path<CijferA, number[]>,
		_context: DashboardContext<CijferI, CijferA, CijfersOverzichtComponent>
	): BarInfo {
		const { className, size, sizeLabel } = makeHistogramBar(attrs, path);
		return {
			className,
			size,
			tooltip: this.createTooltip(path, sizeLabel, size),
		};
	}

	createTooltip(path: Path<CijferA, number[]>, sizeLabel: string, size: number): TooltipElement[] {
		const { cijfer, aantalLeerlingen, aantalCijfers, onvoldoendes } = last(path)!.a;
		if (this.variant() === DashboardVariant.ACTUEEL) {
			return [{ label: sizeLabel, value: `${size}` }];
		} else {
			return [
				{ label: 'Gemiddeld cijfer', value: formatDecimal(cijfer, '1.1-1') },
				{ label: '% onvoldoende', value: `${formatNumber(deelVeilig(onvoldoendes, aantalCijfers) * 100, 'nl-NL', '1.0-0')}%` },
				{ label: 'Leerlingen', value: aantalLeerlingen.toString() },
			];
		}
	}

	override getSelectionConfig(context: DataRequestContext<CijferA>): SelectionConfig<CijferA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('aantalLeerlingen') };
	}

	override getRowClickUrl(
		context: DashboardContext<CijferI, CijferA, CijfersOverzichtComponent>,
		path: Path<CijferA, number[]>
	): UrlTree | undefined {
		const groups = this.variant() === DashboardVariant.ACTUEEL ? [...context.groupNames] : [...context.groupNames, context.subgroupNames[0]];
		const atts: Att[] = ['cf_nm_vak', 'cf_nm_lesgroep', 'cf_nm_leerling'];
		return this.getZoomClickUrl(atts, groups, path) ?? super.getRowClickUrl(context, path);
	}

	partitionMeasure: PartitionMeasure<CijferA> = { type: this.cijferType, getValue: this.cijferValue, format: this.cijferFormat };

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override enrichTableModel(_context: DashboardContext<CijferI, CijferA, CijfersOverzichtComponent>, tableModel: TableModel<DataRow<CijferA>>) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	override createYAxis(context: DashboardContext<CijferI, CijferA, CijfersOverzichtComponent>): Axis {
		// ticks voor 0 t/m 10 met stapjes van 2
		// const ticks = range(0, 12, 2).map((qty) => ({ qty, label: `${qty}` }));
		return { min: 0, max: context.dataRoot!.a.max, ticks: [] };
	}

	protected readonly DashboardVariant = DashboardVariant;
}

export function partitionHistogram<A extends { cf_nr_label_onvoldoendes: number | null }>(
	rowRoot: Level<A & { cijferAfgerondOV: any }, number[]>
): Path<A, number[]>[][] {
	const path = rowRoot.r[0];
	if (!path) return [];
	const a = last(path)!.a;
	const bins = a.cf_nr_label_onvoldoendes === null ? range(0, 11) : ['onvoldoende', 'voldoende'];
	// genereer records met cijferAfgerondOV van 0 t/m 10
	return [bins.map((cijferAfgerondOV) => [...initial(path), { ...last(path)!, a: { ...a, cijferAfgerondOV } }])];
}

export function getCijferSize<I, A extends { cf_nr_label_onvoldoendes: number | null; cijferAfgerondOV: any; aantalCijfers: number }>(
	attrs: I,
	path: Path<A, number[]>
) {
	const { cijferAfgerondOV, cf_nr_label_onvoldoendes, aantalCijfers } = last(path)!.a;
	let size: number;
	if (cijferAfgerondOV === 'onvoldoende') size = cf_nr_label_onvoldoendes!;
	else if (cijferAfgerondOV === 'voldoende') size = aantalCijfers - cf_nr_label_onvoldoendes!;
	else size = get(attrs, 'cf_m_nr_aantal_' + cijferAfgerondOV, 0) as number;
	return size;
}

export function makeHistogramBar<
	I extends CijferHistogram,
	A extends { cf_nr_label_onvoldoendes: number | null; cijferAfgerondOV: any; aantalCijfers: number },
>(attrs: I, path: Path<A, number[]>): { className: string; sizeLabel: string; size: number } {
	const size = getCijferSize(attrs, path);
	const { cijferAfgerondOV } = last(path)!.a;
	const sizeLabel = `Aantal ${cijferOpties(cijferAfgerondOV)}`;
	const className = generateCssClassForCijfer(cijferAfgerondOV);
	return { sizeLabel, size, className };
}
